import { useState, useEffect, useCallback } from "react";
import { Link, useLoaderData } from "react-router-dom";
import { analyticsAPI } from "../../lib/api";
import { DateRangePicker } from "../../components/analytics/DateRangePicker";
import { StatCard } from "../../components/analytics/StatCard";
import { GrowthChart } from "../../components/analytics/GrowthChart";
import { DeviceChart } from "../../components/analytics/DeviceChart";
import { GeoTable } from "../../components/analytics/GeoTable";
import { ExportButton } from "../../components/analytics/ExportButton";
import type { PodcastAnalyticsData } from "../../routes/loaders";
import type { PodcastStats, GrowthPoint, GeoEntry, DeviceEntry } from "../../types/analytics";
import styles from "./PodcastAnalyticsPage.module.css";

function defaultFrom(): Date {
  return new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
}

export default function PodcastAnalyticsPage() {
  const { podcast, organizationId } = useLoaderData() as PodcastAnalyticsData;

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<PodcastStats | null>(null);
  const [growth, setGrowth] = useState<GrowthPoint[]>([]);
  const [geo, setGeo] = useState<GeoEntry[]>([]);
  const [devices, setDevices] = useState<DeviceEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, gr, ge, de] = await Promise.all([
        analyticsAPI.overview(organizationId, podcast.id, from, to),
        analyticsAPI.growth(organizationId, podcast.id, from, to),
        analyticsAPI.geo(organizationId, podcast.id, from, to),
        analyticsAPI.devices(organizationId, podcast.id, from, to),
      ]);
      setStats(s);
      setGrowth(gr);
      setGeo(ge);
      setDevices(de);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [organizationId, podcast.id, from, to]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDateChange = useCallback((newFrom: Date, newTo: Date) => {
    setFrom(newFrom);
    setTo(newTo);
  }, []);

  const exportData = growth.map((p) => ({
    date: p.date,
    downloads: p.downloads,
    streams: p.streams,
    completions: p.completions,
  }));

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.breadcrumb}>
          <Link to={`/organizations/${organizationId}/podcasts`}>Podcasts</Link>
          <span className={styles.sep}>/</span>
          <Link to={`/organizations/${organizationId}/podcasts/${podcast.id}`}>{podcast.name}</Link>
          <span className={styles.sep}>/</span>
          <span>Analytics</span>
        </div>
        <h1 className={styles.title}>Analytics</h1>
      </div>

      <div className={styles.toolbar}>
        <DateRangePicker from={from} to={to} onChange={handleDateChange} />
        <ExportButton data={exportData} filename={`${podcast.name}-analytics`} />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading analytics…</div>
      ) : (
        <>
          <div className={styles.statGrid}>
            <StatCard label="Downloads" value={stats?.totalDownloads ?? 0} />
            <StatCard label="Streams" value={stats?.totalStreams ?? 0} />
            <StatCard label="Completions" value={stats?.totalCompletions ?? 0} />
            <StatCard
              label="Completion Rate"
              value={`${((stats?.completionRate ?? 0) * 100).toFixed(1)}%`}
            />
            <StatCard label="Unique Listeners" value={stats?.uniqueListeners ?? 0} />
          </div>

          <GrowthChart data={growth} />

          <div className={styles.splitGrid}>
            <DeviceChart data={devices} />
            <GeoTable data={geo} />
          </div>
        </>
      )}
    </div>
  );
}
